import React from 'react';
import { FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import AdminFormRow from '../../../../components/admin/AdminFormRow';
import AdminSectionHeading from '../../../../components/admin/AdminSectionHeading';
import type { GameFormData, GameFormErrors } from '../../../../types/features/games';
import {
  adminCheckboxSx,
  adminFormFieldSx,
  adminInlineControlLabelSx,
  adminReadOnlyFieldSx,
} from '../../../../styles/adminComponentStyles';

interface GameDialogFormFieldsProps {
  errors: GameFormErrors;
  formData: GameFormData;
  loading: boolean;
  onInputChange: (field: keyof GameFormData, value: string | boolean) => void;
}

const GameDialogFormFields: React.FC<GameDialogFormFieldsProps> = ({
  errors,
  formData,
  loading,
  onInputChange,
}) => (
  <>
    <AdminSectionHeading>Game Information</AdminSectionHeading>
    <AdminFormRow label="Game Name">
      <TextField
        fullWidth
        size="small"
        placeholder=""
        value={formData.name}
        onChange={(event) => onInputChange('name', event.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        disabled={loading}
        sx={adminFormFieldSx}
        inputProps={{ 'aria-label': 'Game Name' }}
      />
    </AdminFormRow>

    <AdminFormRow label="Game ID" required>
      <TextField
        fullWidth
        size="small"
        placeholder=""
        type="number"
        required
        value={formData.game_id}
        onChange={(event) => onInputChange('game_id', event.target.value)}
        error={!!errors.game_id}
        helperText={errors.game_id}
        disabled={loading}
        sx={adminFormFieldSx}
        inputProps={{ 'aria-label': 'Game ID' }}
      />
    </AdminFormRow>

    <AdminFormRow label="PLU Code">
      <TextField
        fullWidth
        size="small"
        placeholder=""
        type="number"
        value={formData.plu}
        onChange={(event) => onInputChange('plu', event.target.value)}
        error={!!errors.plu}
        helperText={errors.plu}
        disabled={loading}
        sx={adminFormFieldSx}
        inputProps={{ 'aria-label': 'PLU Code' }}
      />
    </AdminFormRow>

    <AdminFormRow label="PLU Verification">
      <RadioGroup
        row
        aria-label="PLU Verification"
        name="pending_plu_verification"
        value={formData.pending_plu_verification ? 'pending' : 'done'}
        onChange={(_, value) => onInputChange('pending_plu_verification', value === 'pending')}
        sx={{ columnGap: 2, rowGap: 1 }}
      >
        <FormControlLabel
          value="pending"
          control={<Radio size="small" sx={adminCheckboxSx} />}
          label="Pending"
          disabled={loading}
          sx={adminInlineControlLabelSx}
        />
        <FormControlLabel
          value="done"
          control={<Radio size="small" sx={adminCheckboxSx} />}
          label="Done"
          disabled={loading}
          sx={adminInlineControlLabelSx}
        />
      </RadioGroup>
    </AdminFormRow>

    <AdminFormRow label="Tickets per Pack" required>
      <TextField
        fullWidth
        size="small"
        placeholder=""
        type="number"
        required
        value={formData.tickets_per_pack}
        onChange={(event) => onInputChange('tickets_per_pack', event.target.value)}
        error={!!errors.tickets_per_pack}
        helperText={errors.tickets_per_pack}
        disabled={loading}
        sx={adminFormFieldSx}
        inputProps={{ 'aria-label': 'Tickets per Pack' }}
      />
    </AdminFormRow>

    <AdminFormRow label="Price per Ticket" required>
      <TextField
        fullWidth
        size="small"
        placeholder=""
        type="number"
        required
        inputProps={{ step: 0.01, min: 0, 'aria-label': 'Price per Ticket' }}
        value={formData.cost_per_ticket}
        onChange={(event) => onInputChange('cost_per_ticket', event.target.value)}
        error={!!errors.cost_per_ticket}
        helperText={errors.cost_per_ticket}
        disabled={loading}
        sx={adminFormFieldSx}
      />
    </AdminFormRow>

    <AdminFormRow label="Price per Pack">
      <Typography sx={adminReadOnlyFieldSx}>
        {formData.price_per_pack ? `$${formData.price_per_pack}` : 'Enter tickets per pack and price per ticket'}
      </Typography>
    </AdminFormRow>
  </>
);

export default React.memo(GameDialogFormFields);
